import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import bgImage from '../images/background.png';

function EdIcon({ name, size = 20 }) {
    const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
    switch (name) {
        case 'user':    return <svg {...p}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
        case 'mail':   return <svg {...p}><rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>;
        case 'lock':   return <svg {...p}><rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;
        case 'arrow-right': return <svg {...p}><path d="M5 12h14M12 5l7 7-7 7"/></svg>;
        case 'arrow-left':  return <svg {...p}><path d="M19 12H5M12 19l-7-7 7-7"/></svg>;
        case 'shield': return <svg {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>;
        default: return null;
    }
}

export default function EditData() {
    const { t } = useTranslation();
    const [userData, setUserData] = useState({ profile: 0 });
    const [formData, setFormData] = useState({
        username: '', email: '', password: '', newPassword: '', confirmNewPassword: ''
    });
    const [errors, setErrors] = useState({});
    const [ready, setReady] = useState(false);
    const [savingProfile, setSavingProfile] = useState(false);
    const [savingPassword, setSavingPassword] = useState(false);
    const navigate = useNavigate();

    useEffect(() => { requestAnimationFrame(() => setReady(true)); }, []);

    const fetchUserData = () => {
        const userLogged = localStorage.getItem('userLogged');
        const userInfo = JSON.parse(userLogged);
        const xhr = new XMLHttpRequest();
        let url = `${process.env.REACT_APP_API_URL}/user/${userInfo.id}`;
        xhr.open("GET", url, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4 && this.status === 200) {
                const userData = JSON.parse(this.responseText);
                setUserData(userData);
                setFormData({ username: userData.username, email: userData.email, password: '', confirmPassword: '' });
            }
        };
        xhr.send();
    };

    useEffect(() => { fetchUserData(); }, []);

    const handleConfirm = async (e) => {
        e.preventDefault();
        setErrors({});

        if (formData.username.length < 2 || formData.username.length > 15) {
            setErrors(errors => ({ ...errors, username: t('errors.username_length') }));
            return;
        }

        if (!/\S+@\S+\.\S+/.test(formData.email)) {
            setErrors(errors => ({ ...errors, email: t('errors.email_format') }));
            return;
        }

        try {
            setSavingProfile(true);
            let xhr = new XMLHttpRequest();
            var data = new FormData();
            data.append('username', formData.username);
            data.append('email', formData.email);

            xhr.open("POST", `${process.env.REACT_APP_API_URL}/users/${userData.id}`, true);
            xhr.onreadystatechange = function () {
                if (this.readyState === 4) {
                    setSavingProfile(false);
                    if (this.status === 200) {
                        const user = JSON.parse(this.responseText);
                        localStorage.setItem('userLogged', JSON.stringify({ username: user.username, profile: user.profile, wines: user.wines, average: user.average, id: user.id }));
                        navigate(-1);
                    }
                }
            };
            xhr.send(data);
        } catch (error) {
            setSavingProfile(false);
            console.error('Error:', error);
        }
    };

    const handleConfirmPassword = async (e) => {
        e.preventDefault();
        setErrors({});

        if (!/^.{8,}$/.test(formData.newPassword)) {
            setErrors(errors => ({ ...errors, password: t('errors.password_length') }));
            return;
        }

        if (formData.newPassword !== formData.confirmNewPassword) {
            setErrors(errors => ({ ...errors, password: t('errors.passwords_no_match') }));
            return;
        }

        try {
            setSavingPassword(true);
            let xhr = new XMLHttpRequest();
            var data = new FormData();
            data.append('current_password', formData.password);
            data.append('new_password', formData.newPassword);

            xhr.open("POST", `${process.env.REACT_APP_API_URL}/pass/${userData.id}`, true);
            xhr.onreadystatechange = function () {
                if (this.readyState === 4) {
                    setSavingPassword(false);
                    if (this.status === 200) {
                        navigate('/');
                    } else {
                        setErrors(errors => ({ ...errors, password: t('errors.update') }));
                    }
                }
            };
            xhr.send(data);
        } catch (error) {
            setSavingPassword(false);
            console.error('Error:', error);
        }
    };

    const handleChange = (e) => {
        const { id, value } = e.target;
        setFormData({ ...formData, [id]: value });
    };

    return (
        <div className={`auth-stage${ready ? ' auth-ready' : ''}`}>
            <div className="auth-bg" style={{ backgroundImage: `url(${bgImage})` }} />
            <div className="auth-vignette" />
            <div className="auth-grain" />

            <div className="auth-card auth-card-register">
                <div className="auth-brand">
                    <EdIcon name="shield" size={14} />
                    <span className="auth-brand-name">newera</span>
                </div>

                <h1 className="auth-heading">{t('edit_data.title')}</h1>

                <form className="auth-form" noValidate>
                    <div className={`auth-field${errors.username ? ' auth-field-err' : ''}`}>
                        <input className="auth-input" id="username" type="text"
                            value={formData.username} onChange={handleChange}
                            placeholder=" " autoComplete="username" maxLength={15} />
                        <label className="auth-label" htmlFor="username">
                            <EdIcon name="user" size={11} />{t('common.username')}
                        </label>
                    </div>
                    {errors.username && <p className="auth-error">{errors.username}</p>}

                    <div className={`auth-field${errors.email ? ' auth-field-err' : ''}`}>
                        <input className="auth-input" id="email" type="email"
                            value={formData.email} onChange={handleChange}
                            placeholder=" " autoComplete="email" />
                        <label className="auth-label" htmlFor="email">
                            <EdIcon name="mail" size={11} />{t('common.email')}
                        </label>
                    </div>
                    {errors.email && <p className="auth-error">{errors.email}</p>}

                    <button type="button" className="auth-submit" onClick={handleConfirm} disabled={savingProfile}>
                        {t('edit_data.change_data')}<EdIcon name="arrow-right" size={15} />
                    </button>
                </form>

                <div className="auth-divider" />

                <form className="auth-form" noValidate>
                    <div className={`auth-field${errors.password ? ' auth-field-err' : ''}`}>
                        <input className="auth-input" id="password" type="password"
                            value={formData.password} onChange={handleChange}
                            placeholder=" " autoComplete="current-password" />
                        <label className="auth-label" htmlFor="password">
                            <EdIcon name="lock" size={11} />{t('common.password')}
                        </label>
                    </div>

                    <div className={`auth-field${errors.password ? ' auth-field-err' : ''}`}>
                        <input className="auth-input" id="newPassword" type="password"
                            value={formData.newPassword} onChange={handleChange}
                            placeholder=" " autoComplete="new-password" />
                        <label className="auth-label" htmlFor="newPassword">
                            <EdIcon name="lock" size={11} />{t('edit_data.label_new_password')}
                        </label>
                    </div>

                    <div className={`auth-field${errors.password ? ' auth-field-err' : ''}`}>
                        <input className="auth-input" id="confirmNewPassword" type="password"
                            value={formData.confirmNewPassword} onChange={handleChange}
                            placeholder=" " autoComplete="new-password" />
                        <label className="auth-label" htmlFor="confirmNewPassword">
                            <EdIcon name="lock" size={11} />{t('edit_data.label_confirm_password')}
                        </label>
                    </div>
                    {errors.password && <p className="auth-error">{errors.password}</p>}

                    <button type="button" className="auth-submit" onClick={handleConfirmPassword} disabled={savingPassword}>
                        {t('edit_data.change_password')}<EdIcon name="arrow-right" size={15} />
                    </button>
                </form>

                <div className="auth-links">
                    <button className="auth-link" onClick={() => navigate(-1)}>
                        <EdIcon name="arrow-left" size={12} />{t('common.back')}
                    </button>
                </div>
            </div>
        </div>
    );
}
